import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useGovStats } from '../hooks/useGovStats';
import { useAuditEvents } from '../hooks/useAuditEvents';
import { useCompanyApplications } from '../hooks/useCompanyApplications';
import {
  Building2, Users, ShieldCheck, DollarSign,
  AlertTriangle, ChevronRight, Activity, TrendingUp,
  CheckCircle, XCircle, Clock,
} from 'lucide-react';

function Skeleton({ className }: { className?: string }) {
  return <div className={`skeleton rounded-xl ${className ?? ''}`} />;
}

const fmtMoney = (n: number) =>
  n.toLocaleString('sv-SE', { style: 'currency', currency: 'SEK', maximumFractionDigits: 0 });

const GovPanel: React.FC = () => {
  const { companies, fleetStats, loading } = useGovStats();
  const { events: auditEvents, loading: auditLoading } = useAuditEvents(30);
  const { applications, loading: appsLoading, approve, reject } = useCompanyApplications('pending');
  const navigate = useNavigate();
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [approvalCreds, setApprovalCreds] = useState<{ email: string; password: string; company: string } | null>(null);
  const [actionError, setActionError] = useState('');

  const auditLabel: Record<string, string> = {
    driver_provisioned: 'Förare provisionerad',
    driver_password_reset: 'Lösenord återställt',
    driver_invite_sent: 'Inbjudan skickad',
    authority_plate_lookup: 'P-ivilotid registreringssökning',
    company_approved: 'Taxibolag godkänt',
    company_rejected: 'Taxibolag avslaget',
  };

  const handleApprove = async (id: string) => {
    setApprovingId(id);
    setActionError('');
    const { data, error } = await approve(id, true);
    setApprovingId(null);
    if (error) { setActionError(error); return; }
    if (data?.password && data.owner_email) {
      setApprovalCreds({
        email: data.owner_email,
        password: data.password,
        company: applications.find((a) => a.id === id)?.company_name ?? 'Company',
      });
    }
  };

  const handleReject = async () => {
    if (!rejectingId) return;
    setActionError('');
    const { error } = await reject(rejectingId, rejectReason.trim() || undefined);
    if (error) { setActionError(error); return; }
    setRejectingId(null);
    setRejectReason('');
  };

  return (
    <Layout
      title="Inspektionspanel"
      breadcrumbs={[{ label: 'Inspektionspanel' }]}
    >
      <div className="space-y-6">

        {/* Gov header */}
        <div className="rounded-2xl p-5 text-white relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #060b17 0%, #0B1120 50%, #0f2044 100%)' }}>
          <div className="absolute top-0 right-0 w-64 h-64 rounded-full -translate-y-24 translate-x-24"
            style={{ background: 'rgba(6,182,212,0.06)', filter: 'blur(40px)' }} />
          <div className="relative">
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck className="w-4 h-4 text-electric-400" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-electric-400">
                Myndighetsnivå — Transportstyrelsen / Skatteverket
              </span>
            </div>
            <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
              Riksöversikt — Taxiflotta Sverige
            </h1>
            <p className="text-white/40 text-xs mt-1">
              {new Date().toLocaleDateString('sv-SE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </div>

        {/* Fleet KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              label: 'Registrerade bolag',
              value: fleetStats.totalCompanies,
              icon: <Building2 className="w-5 h-5" />,
              color: '#06B6D4',
              fmt: (v: number) => v.toString(),
            },
            {
              label: 'Aktiva förare',
              value: fleetStats.totalActiveDrivers,
              icon: <Users className="w-5 h-5" />,
              color: '#8B5CF6',
              fmt: (v: number) => v.toString(),
            },
            {
              label: 'Regelefterlevnad idag',
              value: fleetStats.fleetComplianceRate,
              icon: <ShieldCheck className="w-5 h-5" />,
              color: fleetStats.fleetComplianceRate >= 80 ? '#10b981' : fleetStats.fleetComplianceRate >= 60 ? '#F59E0B' : '#ef4444',
              fmt: (v: number) => `${v}%`,
            },
            {
              label: 'Rapporterad inkomst (mån)',
              value: fleetStats.totalIncomeMonth,
              icon: <DollarSign className="w-5 h-5" />,
              color: '#F59E0B',
              fmt: fmtMoney,
            },
          ].map((stat, i) => (
            <div key={i} className="card p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                style={{ background: `${stat.color}18`, color: stat.color }}>
                {stat.icon}
              </div>
              <div className="min-w-0">
                {loading
                  ? <Skeleton className="h-7 w-16 mb-1" />
                  : <div className="text-2xl font-bold text-slate-800 truncate"
                      style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
                      {stat.fmt(stat.value)}
                    </div>
                }
                <div className="text-xs text-slate-500 leading-tight mt-0.5">{stat.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Pending company applications */}
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-slate-800" style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
                Väntande bolagsansökningar
              </h2>
              <p className="text-xs text-slate-400 mt-0.5">Godkänn för att skapa bolag + company admin-konto</p>
            </div>
            {!appsLoading && applications.length > 0 && (
              <span className="flex items-center gap-1.5 text-xs font-semibold text-amber-600 bg-amber-50 px-2.5 py-1 rounded-lg">
                <Clock className="w-3.5 h-3.5" />
                {applications.length} pending
              </span>
            )}
          </div>
          {actionError && (
            <div className="mx-5 mt-4 px-4 py-2 rounded-xl bg-red-50 text-red-700 text-sm border border-red-100">
              {actionError}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="min-w-full table-premium">
              <thead>
                <tr>
                  <th>Bolag</th>
                  <th>Ägare</th>
                  <th>Org.nr</th>
                  <th>Flotta</th>
                  <th>Inskickad</th>
                  <th>Åtgärd</th>
                </tr>
              </thead>
              <tbody>
                {appsLoading ? (
                  <tr><td colSpan={6} className="text-center py-8 text-slate-400">Laddar…</td></tr>
                ) : applications.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-8 text-slate-400">Inga väntande ansökningar</td></tr>
                ) : applications.map((app) => (
                  <tr key={app.id}>
                    <td>
                      <div className="font-medium text-slate-800 text-sm">{app.company_name}</div>
                      <div className="text-xs text-slate-400">{app.city ?? app.address ?? '—'}</div>
                    </td>
                    <td>
                      <div className="text-sm text-slate-700">{app.owner_full_name}</div>
                      <div className="text-xs text-slate-400">{app.owner_email}</div>
                    </td>
                    <td className="text-sm font-mono">{app.org_number ?? '—'}</td>
                    <td className="text-sm">{app.fleet_size ?? '—'}</td>
                    <td className="text-xs text-slate-400 whitespace-nowrap">
                      {new Date(app.created_at).toLocaleString('sv-SE')}
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleApprove(app.id)}
                          disabled={approvingId === app.id}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-3 py-1.5 rounded-lg transition disabled:opacity-50"
                        >
                          <CheckCircle className="w-3.5 h-3.5" />
                          {approvingId === app.id ? 'Godkänner…' : 'Godkänn'}
                        </button>
                        <button
                          onClick={() => { setRejectingId(app.id); setRejectReason(''); }}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg transition"
                        >
                          <XCircle className="w-3.5 h-3.5" />
                          Avslå
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Companies compliance table */}
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-slate-800" style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
                Taxibolag — Regelefterlevnad
              </h2>
              <p className="text-xs text-slate-400 mt-0.5">Realtidsdata per bolag. Klicka "Granska" för djupgranskning.</p>
            </div>
            <span className="flex items-center gap-1.5 text-xs text-electric-600 font-medium">
              <Activity className="w-3.5 h-3.5" />
              Live
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full table-premium">
              <thead>
                <tr>
                  <th>Bolag</th>
                  <th>Förare</th>
                  <th>Regelefterlevnad</th>
                  <th>Regelbrott</th>
                  <th>Inkomst (mån)</th>
                  <th>Senast aktiv</th>
                  <th>Åtgärd</th>
                </tr>
              </thead>
              <tbody>
                {loading
                  ? [...Array(4)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(7)].map((__, j) => (
                        <td key={j}><div className="skeleton h-4 rounded w-full" /></td>
                      ))}
                    </tr>
                  ))
                  : companies.length === 0
                    ? (
                      <tr>
                        <td colSpan={7} className="text-center py-10 text-sm text-slate-400">
                          Inga registrerade bolag hittades
                        </td>
                      </tr>
                    )
                    : companies.map(({ company, totalDrivers, activeDrivers, complianceRate, violationCount, totalIncomeMonth, lastActive }) => (
                      <tr key={company.id}>
                        <td>
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-xl bg-electric-50 text-electric-500 flex items-center justify-center text-xs font-bold shrink-0">
                              {company.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div className="font-medium text-slate-800 text-sm">{company.name}</div>
                              <div className="text-[10px] text-slate-400">ID: {company.id.slice(0, 8)}…</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="text-sm">
                            <span className="font-semibold text-slate-800">{activeDrivers}</span>
                            <span className="text-slate-400"> / {totalDrivers} aktiva</span>
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 progress-bar" style={{ maxWidth: 80 }}>
                              <div className="progress-bar-fill" style={{
                                width: `${complianceRate}%`,
                                background: complianceRate >= 80
                                  ? 'linear-gradient(90deg,#10b981,#059669)'
                                  : complianceRate >= 60
                                  ? 'linear-gradient(90deg,#F59E0B,#D97706)'
                                  : 'linear-gradient(90deg,#ef4444,#dc2626)',
                              }} />
                            </div>
                            <span className={`text-xs font-bold ${
                              complianceRate >= 80 ? 'text-emerald-600'
                              : complianceRate >= 60 ? 'text-amber-600'
                              : 'text-red-600'
                            }`}>
                              {complianceRate}%
                            </span>
                          </div>
                        </td>
                        <td>
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold border ${
                            violationCount > 0
                              ? 'bg-red-50 text-red-700 border-red-200'
                              : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          }`}>
                            {violationCount > 0 && <AlertTriangle className="w-3 h-3" />}
                            {violationCount}
                          </span>
                        </td>
                        <td className="font-semibold text-slate-700 text-sm">
                          {fmtMoney(totalIncomeMonth)}
                        </td>
                        <td className="text-xs text-slate-400">
                          {lastActive
                            ? new Date(lastActive).toLocaleString('sv-SE', { hour: '2-digit', minute: '2-digit', month: 'short', day: 'numeric' })
                            : '—'}
                        </td>
                        <td>
                          <button
                            onClick={() => navigate(`/tachograph?company=${company.id}`)}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-electric-600 hover:text-electric-800 hover:bg-electric-50 px-3 py-1.5 rounded-lg transition"
                          >
                            Granska
                            <ChevronRight className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))
                }
              </tbody>
            </table>
          </div>

          {/* DAC7 footer note */}
          <div className="px-5 py-3 bg-amber-50/60 border-t border-amber-100 flex items-start gap-2">
            <TrendingUp className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700">
              <strong>DAC7:</strong> Inkomstdata rapporteras automatiskt till Skatteverket per EU-direktiv 2021/514.
              Prop. 2025/26:43 — oföränderlig realtidsdata för skattekontroll.
            </p>
          </div>
        </div>

        {/* Audit trail */}
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <h2 className="font-bold text-slate-800 text-sm" style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
              Granskningslogg
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">Provisionerings- och P-ivilotid-inspektioner</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full table-premium">
              <thead>
                <tr>
                  <th>Tid</th>
                  <th>Händelse</th>
                  <th>Reg.nr</th>
                  <th>Detaljer</th>
                </tr>
              </thead>
              <tbody>
                {auditLoading ? (
                  <tr><td colSpan={4} className="text-center py-8 text-slate-400">Laddar…</td></tr>
                ) : auditEvents.length === 0 ? (
                  <tr><td colSpan={4} className="text-center py-8 text-slate-400">Inga händelser ännu</td></tr>
                ) : auditEvents.map((ev) => (
                  <tr key={ev.id}>
                    <td className="text-xs text-slate-500 whitespace-nowrap">
                      {new Date(ev.created_at).toLocaleString('sv-SE')}
                    </td>
                    <td className="text-sm font-medium text-slate-700">
                      {auditLabel[ev.event_type] ?? ev.event_type}
                    </td>
                    <td className="font-mono text-sm">{ev.plate ?? '—'}</td>
                    <td className="text-xs text-slate-500 max-w-xs truncate">
                      {typeof ev.metadata?.email === 'string' ? ev.metadata.email : ''}
                      {ev.metadata?.email_sent === true ? ' · e-post skickad' : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {approvalCreds && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-modal w-full max-w-md p-6 animate-slide-up">
              <h3 className="font-bold text-slate-800 text-lg mb-1">Bolag godkänt</h3>
              <p className="text-sm text-slate-500 mb-4">
                {approvalCreds.company} — dela inloggning med bolagsägaren.
              </p>
              <div className="space-y-3 bg-slate-50 rounded-xl p-4 font-mono text-sm">
                <div><span className="text-slate-400 text-xs block">E-post</span>{approvalCreds.email}</div>
                <div><span className="text-slate-400 text-xs block">Tillfälligt lösenord</span>{approvalCreds.password}</div>
              </div>
              <button
                onClick={() => {
                  navigator.clipboard?.writeText(`Email: ${approvalCreds.email}\nPassword: ${approvalCreds.password}`);
                  setApprovalCreds(null);
                }}
                className="btn-primary w-full mt-4 text-sm"
              >
                Kopiera och stäng
              </button>
              <button onClick={() => setApprovalCreds(null)} className="btn-ghost w-full mt-2 text-sm">Stäng</button>
            </div>
          </div>
        )}

        {rejectingId && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-modal w-full max-w-md p-6 animate-slide-up">
              <h3 className="font-bold text-slate-800 text-lg mb-1">Avslå ansökan</h3>
              <p className="text-sm text-slate-500 mb-4">Valfri anledning sparas i granskningsloggen.</p>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="input-premium w-full min-h-[80px]"
                placeholder="Anledning (valfritt)"
              />
              <div className="flex gap-2 mt-4">
                <button onClick={handleReject} className="btn-primary text-sm flex-1">Avslå</button>
                <button onClick={() => setRejectingId(null)} className="btn-ghost text-sm flex-1">Avbryt</button>
              </div>
            </div>
          </div>
        )}

      </div>
    </Layout>
  );
};

export default GovPanel;
